import { DatePickerFormField, SelectFormField, TextFormField } from 'components/Forms/FormFields';
import { useFormContext } from 'contexts/formContext';
import { Grid, InputAdornment } from '@mui/material';
import CurrencyFormField, { ICurrencyFormFieldProps } from 'components/Forms/FormFields/CurrencyFormField/CurrencyFormField';
import { creditNoteSelectFilterOptions } from 'contexts/creditNote/creditNoteOptions';
import { sendSubmitEvent } from 'contexts/apiRequestContext';
import { FilterStyleBase } from 'components/DataTableFilters/MethodsForFilters/FilterStyleBase';
import { CurrentValuesSelected } from 'components/DataTableFilters/CurrentValuesSelected/CurrentValuesSelected';
import { ActionSubmitSearch } from 'components/Actions/ActionSubmitSearch';

type CreditApprovFormProps = {
    currentValues: any;
    removeFilter: (index: number, duplicateRemove?: boolean) => void;
} & BatchCreditNoteFiltersFormProps;

type BatchCreditNoteFiltersFormProps = {
    overrideOnClickFilter?: (values: any) => void;
}

function BatchCreditNoteFiltersForm({ overrideOnClickFilter }: BatchCreditNoteFiltersFormProps) {
    const { watch } = useFormContext();

    const onClickSubmit = () => overrideOnClickFilter?.(watch());

    const currencyDefaultProps = (name: string) => {
        return {
            name,
            fullWidth: true,
            variant: "outlined",
            required: true,
            showEndAdornment: true,
            endAdornmentType: 'button',
            overrideOnClickFilter: onClickSubmit,
        } as ICurrencyFormFieldProps
    };

    return (
        <>
            <SelectFormField
                name="optionFilter"
                label="Filtrar por"
                variant="outlined"
                fullWidth
                options={[
                    { label: 'Número da operação', value: 'creditNoteNo' },
                    { label: 'Status', value: 'status' },
                    { label: 'Data de início', value: 'initialDate' },
                    { label: 'Valor máximo', value: 'maxValue' },
                    { label: 'Valor mínimo', value: 'minValue' },
                    { label: 'Valor de Cessão (Máximo)', value: 'maxAssignmentValue' },
                    { label: 'Valor de Cessão (Mínimo)', value: 'minAssignmentValue' },
                    { label: 'Correspondente', value: 'tenant' },
                ]}
            />

            {watch('optionFilter') === 'creditNoteNo' && (
                <TextFormField
                    label="Filtrar por número da operação"
                    name="creditNoteNo"
                    variant="outlined"
                    fullWidth
                    type="number"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ marginRight: -5 }}>
                                <span
                                    style={{
                                        display: 'block',
                                        fontSize: '20px',
                                    }}
                                >
                                    #
                                </span>
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <ActionSubmitSearch typeButton='button' onClick={onClickSubmit}/>
                        ),
                    }}
                />
            )}
            {watch('optionFilter') === 'status' && (
                <SelectFormField
                    label="Filtrar por status"
                    name="status"
                    fullWidth
                    showEndAdornment
                    endAdornmentType='button'
                    options={creditNoteSelectFilterOptions}
                    onClick={sendSubmitEvent}
                    overrideOnClickFilter={onClickSubmit}
                />
            )}
            {watch('optionFilter') === 'maxAssignmentValue' && (
                <CurrencyFormField
                    label="Valor de Cessão (Máximo)"
                    {...currencyDefaultProps('maxAssignmentValue')}
                />
            )}
            {watch('optionFilter') === 'minAssignmentValue' && (
                <CurrencyFormField
                    label="Valor de Cessão (Mínimo)"
                    {...currencyDefaultProps('minAssignmentValue')}
                />
            )}
            {watch('optionFilter') === 'maxValue' && (
                <CurrencyFormField
                    label="Valor máximo"
                    {...currencyDefaultProps('maxValue')}
                />
            )}
            {watch('optionFilter') === 'minValue' && (
                <CurrencyFormField
                    label="Valor mínimo"
                    {...currencyDefaultProps('minValue')}
                />
            )}

            {watch('optionFilter') === 'initialDate' && (
                <Grid
                    sx={{
                        display: 'grid',
                        alignItems: 'center',
                        gridTemplateColumns: 'auto auto',
                        gridGap: 10,
                    }}
                >
                    <DatePickerFormField
                        label="Filtrar por data inicial"
                        name="initialDate"
                        fullWidth
                        required
                    />
                    <DatePickerFormField
                        label="Filtrar por data final"
                        name="finalDate"
                        fullWidth
                        required
                        componentFilter={
                            <ActionSubmitSearch typeButton='button' onClick={onClickSubmit} />
                        }
                    />
                </Grid>
            )}
        </>
    );
}

export const BatchCreditNoteFilters = ({ currentValues, removeFilter, overrideOnClickFilter }: CreditApprovFormProps) => {
    return (
        <FilterStyleBase
            formFilters={<BatchCreditNoteFiltersForm overrideOnClickFilter={overrideOnClickFilter} />}
            currentValues={
                <CurrentValuesSelected
                    currentValues={currentValues}
                    removeFilterSelected={removeFilter}
                />
            }
        />
    );
};
